/**
 * Occasion Name Prompt Modal
 * 
 * Reusable modal component for prompting users to name an occasion
 * Follows 4RKED design system
 */

import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

const OccasionNamePrompt = ({ isOpen, onClose, onContinue, title = 'Name Your Occasion' }) => {
    const [occasionName, setOccasionName] = useState('');
    const [error, setError] = useState('');
    const inputRef = useRef(null);

    useEffect(() => {
        if (isOpen && inputRef.current) {
            inputRef.current.focus();
        }
    }, [isOpen]);

    useEffect(() => {
        if (isOpen) {
            // Prevent body scroll when modal is open
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
            setOccasionName('');
            setError('');
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [isOpen]);

    const handleContinue = () => {
        const trimmedName = occasionName.trim();
        if (!trimmedName) {
            setError('Please enter a name for your occasion');
            return;
        }
        onContinue(trimmedName);
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleContinue();
        } else if (e.key === 'Escape') {
            onClose();
        }
    };

    if (!isOpen) return null;

    const modalContent = (
        <div 
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
            onClick={onClose}
        >
            <div 
                className="bg-neutral-0 p-6 hairline max-w-md w-full mx-4"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="mb-6">
                    <h2 className="font-sans text-3xl font-medium text-[#1b263b] mb-2">
                        {title}
                    </h2>
                    <p className="font-sans text-base text-neutral-900 opacity-60">
                        Give your occasion a name to help you organize your recipes
                    </p>
                </div>

                <div className="mb-6">
                    <label className="block font-mono text-xs uppercase tracking-wider text-neutral-900 mb-2">
                        Occasion Name
                    </label>
                    <input
                        ref={inputRef}
                        type="text"
                        value={occasionName}
                        onChange={(e) => {
                            setOccasionName(e.target.value);
                            setError('');
                        }}
                        onKeyDown={handleKeyPress}
                        className="w-full px-4 py-2 font-sans text-base bg-neutral-0 text-neutral-900 hairline focus:outline-none focus:ring-2 focus:ring-[#1b263b]"
                        placeholder="e.g., Sunday Brunch, Holiday Dinner"
                        style={{ color: '#0D0D0D' }}
                    />
                    {error && (
                        <p className="mt-2 font-sans text-sm text-red-600">{error}</p>
                    )}
                </div>

                <div className="flex gap-4 justify-end">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 font-mono text-sm uppercase tracking-wider bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-900 hover:text-neutral-0 transition-all"
                    >
                        CANCEL
                    </button>
                    <button
                        onClick={handleContinue}
                        className="px-4 py-2 font-mono text-sm uppercase tracking-wider transition-all border-none"
                        style={{ backgroundColor: '#1b263b', color: '#FFFFFF' }}
                        onMouseEnter={(e) => e.target.style.opacity = '0.6'}
                        onMouseLeave={(e) => e.target.style.opacity = '1'}
                    >
                        CONTINUE
                    </button>
                </div>
            </div>
        </div>
    );

    return createPortal(modalContent, document.body);
};

export default OccasionNamePrompt;

